import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Utensils, ChefHat, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        // Filter items that have actual uploaded images (from emergentagent.com)
        const itemsWithImages = response.data.filter(item => 
          item.image_url && 
          item.image_url.includes('emergentagent.com')
        );
        setMenuItems(itemsWithImages);
      } catch (error) {
        console.error('Error fetching menu items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenuItems();
  }, []);

  // Group items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen pt-20 md:pt-24 bg-gradient-to-b from-silver-bright to-white" data-testid="blog-page">
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1729809106394-7ceb922b37f0?w=1920&q=80"
            alt="Stories and Insights"
            className="w-full h-full object-cover"
          />
          <div className="page-hero-overlay" />
        </div>
        <div className="page-hero-content">
          <div className="max-w-3xl">
            <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Culinary Stories
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
              Our Signature Dishes
            </h1>
            <p className="text-white/80 text-lg">
              Explore the artistry behind each creation from our kitchen - 
              authentic flavours crafted with passion and precision.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Dishes Section */}
      <section className="section-padding">
        <div className="container-elegant">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="card-elegant animate-pulse">
                  <div className="aspect-[4/3] bg-malva/10" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-malva/10 rounded w-3/4" />
                    <div className="h-4 bg-malva/10 rounded w-full" />
                    <div className="h-4 bg-malva/10 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : menuItems.length === 0 ? (
            <div className="text-center py-16">
              <ChefHat className="w-16 h-16 text-malva/30 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Our culinary stories are coming soon...</p>
              <p className="text-gray-400 text-sm mt-2">Check back later to discover our signature dishes.</p>
            </div>
          ) : (
            <>
              {/* Featured Hero Dish */}
              {menuItems.length > 0 && (
                <div className="mb-16">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={menuItems[0].image_url}
                        alt={menuItems[0].name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="w-5 h-5 text-malva-dark fill-malva-dark" />
                        <span className="text-malva-dark text-sm font-medium uppercase tracking-wider">Featured Dish</span>
                      </div>
                      <p className="text-malva text-sm font-sans tracking-widest uppercase mb-2">
                        {menuItems[0].category}
                      </p>
                      <h2 className="font-serif text-3xl md:text-4xl text-gray-800 mb-4">
                        {menuItems[0].name}
                      </h2>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {menuItems[0].description}
                      </p>
                      <Link 
                        to="/menu"
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="view-menu-btn"
                      >
                        <Utensils className="w-4 h-4" />
                        View Full Menu
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Dishes by Category */}
              {Object.entries(groupedItems).map(([category, items]) => (
                <div key={category} className="mb-16">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 rounded-full bg-malva/20 flex items-center justify-center">
                      <ChefHat className="w-6 h-6 text-malva-dark" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl text-gray-800">{category}</h3>
                      <p className="text-gray-500 text-sm">{items.length} signature {items.length === 1 ? 'dish' : 'dishes'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {items.map((item, index) => (
                      <article 
                        key={item.id} 
                        className="card-elegant group"
                        data-testid={`menu-item-${index}`}
                      >
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                        </div>
                        <div className="p-6 bg-gradient-to-b from-white to-silver-bright/30">
                          <p className="text-malva text-xs font-sans tracking-widest uppercase mb-2">
                            {item.category}
                          </p>
                          <h4 className="font-serif text-xl text-gray-800 mb-3 group-hover:text-malva-dark transition-colors duration-300">
                            {item.name}
                          </h4>
                          <p className="text-gray-500 text-sm line-clamp-3">
                            {item.description}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}

              {/* Call to Action */}
              <div className="text-center mt-12 pt-12 border-t border-malva/10">
                <p className="text-gray-500 mb-6">
                  Experience these dishes and many more at Reva Dining
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/menu"
                    className="btn-secondary inline-flex items-center justify-center gap-2"
                  >
                    <Utensils className="w-4 h-4" />
                    Explore Full Menu
                  </Link>
                  <Link 
                    to="/booking"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                  >
                    Reserve a Table
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
